import { WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID } from '@cotera/utilities';
import { WorkflowNodeType } from './types';
import type { SerializedWorkflowNode } from './types';
import { WorkflowNode } from './workflow-node';
import { resolveNoWorkflow } from './workflow-node-base';
import type { WorkflowNodeOutputShapeContext } from './workflow-node-output-shape';
import { sampleJsonFromOutputShape } from './sample-json-from-output-shape';
import './workflow-node-from-serialized';

export type GraphNodeForOutputSample = {
  readonly id: string;
  readonly type: WorkflowNodeType | string;
  readonly data?: unknown;
  readonly inputShape?: string | null;
  readonly defaultOutput?: unknown | null;
};

export type GraphEdgeForOutputSample = {
  readonly fromNodeId: string;
  readonly toNodeId: string;
};

const PLACEHOLDER_WORKFLOW_NODE_FIELDS = {
  ownerId: '00000000-0000-4000-8000-000000000001',
  ownerOrgId: '00000000-0000-4000-8000-000000000001',
  workflowId: '00000000-0000-4000-8000-000000000002',
  inputFromNodeId: null,
  createdAt: '1970-01-01T00:00:00.000Z',
  updatedAt: '1970-01-01T00:00:00.000Z',
} as const;

export function workflowNodeFromGraphNode(
  node: GraphNodeForOutputSample
): WorkflowNode {
  const serialized: SerializedWorkflowNode = {
    id: node.id,
    ...PLACEHOLDER_WORKFLOW_NODE_FIELDS,
    type: node.type as WorkflowNodeType,
    data: node.data ?? {},
    inputShape: node.inputShape ?? null,
    defaultOutput: node.defaultOutput ?? null,
  };
  return WorkflowNode.fromSerialized(serialized, {
    loadWorkflow: resolveNoWorkflow,
  });
}

export function buildWorkflowNodeOutputShapeContext(params: {
  readonly nodes: readonly GraphNodeForOutputSample[];
  readonly edges?: readonly GraphEdgeForOutputSample[];
  readonly resolveAgentOutputShape: (agentId: string) => string | null;
  readonly resolveToolOutputShape: (toolKey: string) => string | null;
  readonly resolveWebhookRunOutputShape: () => string | null;
  /**
   * The payload skeleton this workflow's trigger hands its entry node, exposed
   * to binding refs under {@link WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID}. Optional
   * because only callers that can describe the trigger (the editor, the save
   * validator) have it; without it a `__trigger__` ref simply types as `super`
   * rather than being reported wrong.
   */
  readonly resolveTriggerSample?: () => unknown | null;
}): WorkflowNodeOutputShapeContext {
  const nodeById = new Map(params.nodes.map((node) => [node.id, node]));
  /** Nodes currently mid-resolution, so a cyclic graph can't recurse forever. */
  const resolvingSampleFor = new Set<string>();
  /**
   * The same guard for shapes.
   *
   * Shape resolution cycles without any cycle in the *edges*: a for-each
   * derives its shape from its loop body, and a pass-through body (filter,
   * debounce) derives its own from its predecessor — which is the for-each.
   * That round trip is a legal graph and used to recurse until the stack blew.
   */
  const resolvingShapeFor = new Set<string>();

  const predecessorsByNodeId = new Map<string, string[]>();
  for (const edge of params.edges ?? []) {
    const existing = predecessorsByNodeId.get(edge.toNodeId);
    if (existing === undefined) {
      predecessorsByNodeId.set(edge.toNodeId, [edge.fromNodeId]);
    } else {
      existing.push(edge.fromNodeId);
    }
  }

  const context: WorkflowNodeOutputShapeContext = {
    resolveAgentOutputShape: params.resolveAgentOutputShape,
    resolveToolOutputShape: params.resolveToolOutputShape,
    resolveWebhookRunOutputShape: params.resolveWebhookRunOutputShape,
    resolveNodeOutputShape: (nodeId) => {
      const graphNode = nodeById.get(nodeId);
      if (graphNode === undefined) {
        return null;
      }
      if (resolvingShapeFor.has(nodeId)) {
        return null;
      }
      resolvingShapeFor.add(nodeId);
      try {
        return workflowNodeFromGraphNode(graphNode).deriveOutputShape(context);
      } finally {
        resolvingShapeFor.delete(nodeId);
      }
    },
    resolvePredecessorOutputShape: (nodeId) => {
      const predecessors = predecessorsByNodeId.get(nodeId) ?? [];
      // Only pass through with an unambiguous single predecessor; fan-in shape
      // is a merged record we can't represent here.
      if (predecessors.length !== 1) {
        return null;
      }
      return context.resolveNodeOutputShape(predecessors[0]!);
    },
    resolveUpstreamSample: (sourceId) => {
      if (sourceId === WORKFLOW_TRIGGER_UPSTREAM_SOURCE_ID) {
        return params.resolveTriggerSample?.() ?? null;
      }
      const graphNode = nodeById.get(sourceId);
      if (graphNode === undefined) {
        return null;
      }
      // A node whose own output is assembled from upstream refs resolves its
      // sources through this same resolver, so a graph that (wrongly) cycles
      // would recurse forever. Refuse to re-enter a node already being
      // resolved rather than trusting the graph to be acyclic.
      if (resolvingSampleFor.has(sourceId)) {
        return null;
      }
      resolvingSampleFor.add(sourceId);
      try {
        return resolveWorkflowNodeOutputSample(graphNode, context);
      } finally {
        resolvingSampleFor.delete(sourceId);
      }
    },
    resolvePredecessorOutputSample: (nodeId) => {
      const predecessors = predecessorsByNodeId.get(nodeId) ?? [];
      if (predecessors.length !== 1) {
        return null;
      }
      return context.resolveUpstreamSample?.(predecessors[0]!) ?? null;
    },
    hasGraphPredecessor: (nodeId) =>
      (predecessorsByNodeId.get(nodeId)?.length ?? 0) > 0,
  };

  return context;
}

export function resolveWorkflowNodeOutputSample(
  node: GraphNodeForOutputSample,
  context: WorkflowNodeOutputShapeContext
): unknown | null {
  const workflowNode = workflowNodeFromGraphNode(node);
  const defaultOutput = workflowNode.defaultOutput;
  if (defaultOutput !== null && defaultOutput !== undefined) {
    return defaultOutput;
  }
  return sampleJsonFromOutputShape(workflowNode.deriveOutputShape(context));
}
